import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockRfqManagementService } from '@/services/mock/MockRfqManagementService';
import { RfqRecord } from '@/types/domain/rfq-management';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Save, Layers, ShieldCheck, Calendar, Truck } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const RfqEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [rfq, setRfq] = useState<RfqRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('MEDIUM');
  const [estimatedTotalAmount, setEstimatedTotalAmount] = useState<number>(0);
  const [closingDate, setClosingDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [incoterms, setIncoterms] = useState('');
  const [warrantyPeriod, setWarrantyPeriod] = useState('');

  useEffect(() => {
    const loadRfq = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const data = await mockRfqManagementService.getRfqById(id);
        if (!data) {
          navigate(ROUTES.RFQ.LIST);
          return;
        }
        setRfq(data);
        setTitle(data.title || '');
        setDescription(data.description || '');
        setCategory(data.category || '');
        setPriority(data.priority || 'MEDIUM');
        setEstimatedTotalAmount(data.estimatedTotalAmount || 0);
        setClosingDate(data.closingDate ? data.closingDate.split('T')[0]! : '');
        setPaymentTerms(data.commercialTerms?.paymentTerms || 'Net 30');
        setIncoterms(data.commercialTerms?.incoterms || 'DDP');
        setWarrantyPeriod(data.commercialTerms?.warrantyPeriod || '24 Months');
      } finally {
        setIsLoading(false);
      }
    };

    loadRfq();
  }, [id, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !rfq) return;

    try {
      setIsSaving(true);
      await mockRfqManagementService.updateRfq(id, {
        title,
        description,
        category,
        priority,
        estimatedTotalAmount: Number(estimatedTotalAmount),
        closingDate: `${closingDate}T17:00:00Z`,
        commercialTerms: {
          ...rfq.commercialTerms,
          paymentTerms,
          incoterms,
          warrantyPeriod,
        },
      });
      navigate(ROUTES.RFQ.DETAILS(id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update RFQ.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Strategic RFQ Editor...
      </div>
    );
  }

  if (!rfq) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">RFQ Sourcing Package Not Found</h2>
        <Button size="sm" onClick={() => navigate(ROUTES.RFQ.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to RFQ Workspace
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.RFQ.DETAILS(id!))}
            className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-primary px-2.5 py-0.5 bg-primary/10 rounded-lg border border-primary/20">
                {rfq.rfqNumber}
              </span>
              <h1 className="text-xl font-extrabold text-foreground tracking-tight font-poppins">
                Edit Sourcing Package & Milestones
              </h1>
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
              Source Requisition: <span className="font-mono font-bold text-primary">{rfq.sourceRequestId}</span> • SAP PR: {rfq.sapPrNumber}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.RFQ.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-rfq-form"
            size="sm"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </div>

      <form id="edit-rfq-form" onSubmit={handleSave} className="space-y-6">
        {/* Section 1: Sourcing Scope & Categorization */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <Layers className="h-4 w-4 text-primary" />
              Sourcing Package Scope & Priority
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div>
              <label className="block font-bold text-foreground mb-1.5">
                RFQ Sourcing Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary font-poppins"
                placeholder="e.g. FY26 Enterprise Data Center Blade Servers & Switches"
              />
            </div>

            <div>
              <label className="block font-bold text-foreground mb-1.5">
                Sourcing Scope & Bidding Narrative *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={3}
                className="w-full rounded-xl border border-input bg-background p-3 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary font-poppins leading-relaxed"
                placeholder="Provide detailed instructions, delivery expectations, and bidding requirements for invited suppliers..."
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Procurement Category *
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="IT Hardware">IT Hardware</option>
                  <option value="Facilities & Services">Facilities & Services</option>
                  <option value="Consulting Services">Consulting Services</option>
                  <option value="Raw Materials">Raw Materials</option>
                  <option value="CapitalEquipment">Capital Equipment</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Sourcing Priority *
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as any)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="LOW">Low Priority</option>
                  <option value="MEDIUM">Medium Priority</option>
                  <option value="HIGH">High Priority</option>
                  <option value="CRITICAL">Critical / Rush</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Estimated Sourcing Budget (₹ INR) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono font-bold text-foreground-muted">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={estimatedTotalAmount}
                    onChange={(e) => setEstimatedTotalAmount(Number(e.target.value))}
                    required
                    min={0}
                    step={1}
                    className="w-full rounded-xl border border-input bg-background pl-8 pr-3 py-2.5 text-xs font-bold text-foreground font-mono focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Bidding Window & Commercial Clauses */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Bidding Deadlines & Commercial Baseline
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-primary" /> Submission Closing Date *
                </label>
                <input
                  type="date"
                  value={closingDate}
                  onChange={(e) => setClosingDate(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Payment Baseline *
                </label>
                <select
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="Net 30">Net 30 Days</option>
                  <option value="Net 45">Net 45 Days</option>
                  <option value="Net 60">Net 60 Days</option>
                  <option value="20% Advance, 80% on Delivery">20% Advance, 80% on Delivery</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Truck className="h-3.5 w-3.5 text-primary" /> Delivery Incoterms *
                </label>
                <select
                  value={incoterms}
                  onChange={(e) => setIncoterms(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="DDP">DDP — Delivered Duty Paid</option>
                  <option value="DAP">DAP — Delivered At Place</option>
                  <option value="FOB">FOB — Free On Board</option>
                  <option value="CIF">CIF — Cost, Insurance & Freight</option>
                  <option value="EXW">EXW — Ex Works</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Warranty Requirement *
                </label>
                <input
                  type="text"
                  value={warrantyPeriod}
                  onChange={(e) => setWarrantyPeriod(e.target.value)}
                  required
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-bold text-foreground focus:ring-2 focus:ring-primary"
                  placeholder="e.g. 24 Months On-Site"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Controls */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.RFQ.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer px-6 shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};
